import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, View, Pressable, FlatList, TextInput, ActivityIndicator, Alert } from 'react-native';
import { Text, Title } from '@constants/Themed';
import { YStack, XStack, Card, ScrollView } from 'tamagui';
import { MaterialIcons, Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Button from '@components/UI/Button/Button';
import apis from '@api/api';
import useGlobalUser from '@hooks/getGlobalUser';
import styles from '@stylesheet';

export interface DataModelSelectionModalHandle {
  openModal: (selectedPOIs: any[]) => void;
  closeModal: () => void;
}

interface DataModelSelectionModalProps {
  onConfirm: (selectedPOIs: any[], dataModelId: string | null, createNew: boolean, modelName?: string) => Promise<void>;
}

const DataModelSelectionModal = forwardRef<DataModelSelectionModalHandle, DataModelSelectionModalProps>(
  ({ onConfirm }, ref) => {
    const [visible, setVisible] = useState(false);
    const [selectedPOIs, setSelectedPOIs] = useState<any[]>([]);
    const [dataModels, setDataModels] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
    const [creatingNew, setCreatingNew] = useState(false);
    const [newModelName, setNewModelName] = useState('Networking Leads');
    const [searchQuery, setSearchQuery] = useState('');
    const [showCompatibilityWarning, setShowCompatibilityWarning] = useState(false);
    const [compatibilityReport, setCompatibilityReport] = useState<any>(null);
    const [selectedModelForValidation, setSelectedModelForValidation] = useState<any>(null);

    const theme = themeSelector();
    const userId = useGlobalUser();

    const colors = {
      background: theme ? '#ffffff' : '#1f2937',
      cardBackground: theme ? '#f8fafc' : '#374151',
      text: theme ? '#1f2937' : '#f9fafb',
      textSecondary: theme ? '#6b7280' : '#9ca3af',
      border: theme ? '#e5e7eb' : '#4b5563',
      accent: '#3b82f6',
      success: '#10b981',
      smartBlue: 'rgb(90,192,234)',
    };

    useImperativeHandle(ref, () => ({
      openModal: (pois: any[]) => {
        setSelectedPOIs(pois);
        setVisible(true);
        fetchDataModels();
      },
      closeModal: () => {
        setVisible(false);
        resetState();
      }
    }));

    const resetState = () => {
      setSelectedModelId(null);
      setCreatingNew(false);
      setNewModelName('Networking Leads');
      setSearchQuery('');
      setSelectedPOIs([]);
      setShowCompatibilityWarning(false);
      setCompatibilityReport(null);
      setSelectedModelForValidation(null);
    };

    /**
     * Compare POI fields with existing data model schema
     * Returns compatibility report with mismatch details
     */
    const compareFieldsWithSchema = (pois: any[], schema: any[]): any => {
      if (!pois || pois.length === 0 || !schema || schema.length === 0) {
        return { compatible: true, mismatches: [], matchRate: 100 };
      }

      // Get all fields from first POI (assuming similar structure)
      const samplePOI = pois[0];
      const poiFields = Object.keys(samplePOI).reduce((acc, key) => {
        acc[key.toLowerCase().replace(/[_\s]/g, '')] = {
          originalKey: key,
          type: typeof samplePOI[key],
          sample: samplePOI[key]
        };
        return acc;
      }, {} as any);

      // Get schema fields
      const schemaFields = schema.reduce((acc, field) => {
        const normalizedTitle = field.title.toLowerCase().replace(/[_\s]/g, '');
        acc[normalizedTitle] = {
          originalTitle: field.title,
          type: field.type,
          options: field.options
        };
        return acc;
      }, {} as any);

      const mismatches: any[] = [];
      const matches: any[] = [];
      let typeMismatches = 0;

      // Check each schema field against POI fields
      Object.keys(schemaFields).forEach(normalizedKey => {
        const schemaField = schemaFields[normalizedKey];
        const poiField = poiFields[normalizedKey];

        if (poiField) {
          // Field exists in both - check type compatibility
          if (poiField.type !== schemaField.type &&
            !(poiField.type === 'object' && schemaField.type === 'string') &&
            !(poiField.type === 'string' && schemaField.type === 'number')) {
            typeMismatches++;
            mismatches.push({
              field: schemaField.originalTitle,
              poiField: poiField.originalKey,
              schemaType: schemaField.type,
              poiType: poiField.type,
              severity: 'high',
              message: `Type mismatch: Schema expects ${schemaField.type}, POI has ${poiField.type}`
            });
          } else {
            matches.push({
              field: schemaField.originalTitle,
              poiField: poiField.originalKey
            });
          }
        } else {
          // Field missing in POI
          if (!['email', 'phone', 'alignment_score', 'business_summary', 'enrichment_status'].includes(normalizedKey)) {
            mismatches.push({
              field: schemaField.originalTitle,
              poiField: null,
              schemaType: schemaField.type,
              poiType: null,
              severity: 'medium',
              message: `Field "${schemaField.originalTitle}" exists in schema but not in POI data`
            });
          }
        }
      });

      // Calculate match rate
      const totalFields = schema.length;
      const matchedFields = matches.length;
      const matchRate = totalFields > 0 ? Math.round((matchedFields / totalFields) * 100) : 0;

      // Determine compatibility
      const highSeverityCount = mismatches.filter(m => m.severity === 'high').length;
      const compatible = matchRate >= 50 && highSeverityCount < 3;

      return {
        compatible,
        matchRate,
        matches,
        mismatches,
        typeMismatches,
        totalFields,
        matchedFields,
        recommendation: compatible ?
          'Fields are reasonably compatible' :
          'Significant field mismatch detected. Consider creating a new data model.'
      };
    };

    const fetchDataModels = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        const models = await apis.getDataFolders({
          userId,
          dataFilter: 'all'
        });

        // Filter for models that might be relevant for networking/leads
        const relevantModels = models.filter((model: any) => {
          const tags = model.dataTags || [];
          return tags.some((tag: string) =>
            ['customer', 'lead', 'networking', 'contact', 'business'].includes(tag.toLowerCase())
          );
        });

        setDataModels(relevantModels);
      } catch (error) {
        console.error('Error fetching data models:', error);
        setDataModels([]);
      } finally {
        setLoading(false);
      }
    };

    const handleConfirm = async () => {
      if (!creatingNew && !selectedModelId) {
        alert('Please select a data model or choose to create a new one');
        return;
      }

      if (creatingNew && !newModelName.trim()) {
        alert('Please enter a name for the new data model');
        return;
      }

      // If adding to existing model, validate field compatibility
      if (!creatingNew && selectedModelId) {
        const selectedModel = dataModels.find(m => m._id === selectedModelId);
        if (selectedModel && selectedModel.dataSchema) {
          const report = compareFieldsWithSchema(selectedPOIs, selectedModel.dataSchema);

          // If not compatible or match rate is low, show warning
          if (!report.compatible || report.matchRate < 60) {
            setCompatibilityReport(report);
            setSelectedModelForValidation(selectedModel);
            setShowCompatibilityWarning(true);
            return; // Wait for user decision
          }
        }
      }

      // Proceed with adding POIs
      await proceedWithAddition();
    };

    const proceedWithAddition = async () => {
      setSubmitting(true);
      try {
        await onConfirm(
          selectedPOIs,
          creatingNew ? null : selectedModelId,
          creatingNew,
          creatingNew ? newModelName : undefined
        );
        setVisible(false);
        resetState();
      } catch (error) {
        console.error('Error confirming selection:', error);
        alert('Failed to add POIs. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    const filteredModels = dataModels.filter(model => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        model.dataTitle?.toLowerCase().includes(query) ||
        model.dataTags?.some((tag: string) => tag.toLowerCase().includes(query))
      );
    });

    const renderModelItem = ({ item }: { item: any }) => (
      <Pressable
        onPress={() => {
          setSelectedModelId(item._id);
          setCreatingNew(false);
        }}
        style={[
          styles.defaultShadow,
          {
            marginVertical: 8,
            marginHorizontal: 4,
            borderRadius: 12,
            overflow: 'hidden'
          }
        ]}
      >
        <Card
          padding={16}
          backgroundColor={selectedModelId === item._id ? colors.accent : colors.cardBackground}
          borderRadius={12}
          borderWidth={2}
          borderColor={selectedModelId === item._id ? colors.accent : colors.border}
        >
          <XStack alignItems="center" gap={12}>
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                backgroundColor: selectedModelId === item._id ? 'rgba(255,255,255,0.2)' : colors.accent,
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <MaterialIcons
                name="folder"
                size={24}
                color={selectedModelId === item._id ? 'white' : colors.cardBackground}
              />
            </View>
            <YStack flex={1}>
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: selectedModelId === item._id ? 'white' : colors.text
                }}
              >
                {item.dataTitle?.replace(/_/g, ' ').toUpperCase() || 'Untitled Model'}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: selectedModelId === item._id ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                  marginTop: 4
                }}
              >
                {item.count || 0} records • {item.dataTags?.join(', ') || 'No tags'}
              </Text>
            </YStack>
            {selectedModelId === item._id && (
              <Ionicons name="checkmark-circle" size={24} color="white" />
            )}
          </XStack>
        </Card>
      </Pressable>
    );

    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setVisible(false);
          resetState();
        }}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}
        >
          <Card
            backgroundColor={colors.background}
            padding={24}
            borderRadius={16}
            width="100%"
            maxWidth={600}
            maxHeight="80%"
            shadowColor="#000"
            shadowOffset={{ width: 0, height: 4 }}
            shadowOpacity={0.3}
            shadowRadius={12}
            elevation={12}
          >
            {/* Header */}
            <XStack justifyContent="space-between" alignItems="center" marginBottom={20}>
              <YStack>
                <Title style={{ color: colors.text, fontSize: 24, fontWeight: '700' }}>
                  Add to Data Model
                </Title>
                <Text style={{ color: colors.textSecondary, fontSize: 14, marginTop: 4 }}>
                  {selectedPOIs.length} businesses selected
                </Text>
              </YStack>
              <Pressable onPress={() => setVisible(false)}>
                <Ionicons name="close" size={28} color={colors.textSecondary} />
              </Pressable>
            </XStack>

            {/* Create New Option */}
            <Pressable
              onPress={() => {
                setCreatingNew(true);
                setSelectedModelId(null);
              }}
              style={[
                styles.defaultShadow,
                {
                  marginBottom: 16,
                  borderRadius: 12,
                  overflow: 'hidden'
                }
              ]}
            >
              <Card
                padding={16}
                backgroundColor={creatingNew ? colors.success : colors.cardBackground}
                borderRadius={12}
                borderWidth={2}
                borderColor={creatingNew ? colors.success : colors.border}
              >
                <XStack alignItems="center" gap={12}>
                  <View
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 24,
                      backgroundColor: creatingNew ? 'rgba(255,255,255,0.2)' : colors.success,
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <MaterialIcons
                      name="add-circle"
                      size={24}
                      color={creatingNew ? 'white' : colors.cardBackground}
                    />
                  </View>
                  <YStack flex={1}>
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: '600',
                        color: creatingNew ? 'white' : colors.text
                      }}
                    >
                      Create New Data Model
                    </Text>
                    {creatingNew && (
                      <TextInput
                        value={newModelName}
                        onChangeText={setNewModelName}
                        placeholder="Enter model name..."
                        placeholderTextColor={creatingNew ? 'rgba(255,255,255,0.6)' : colors.textSecondary}
                        style={{
                          marginTop: 8,
                          padding: 8,
                          borderRadius: 8,
                          backgroundColor: creatingNew ? 'rgba(255,255,255,0.2)' : colors.border,
                          color: creatingNew ? 'white' : colors.text,
                          fontSize: 14
                        }}
                      />
                    )}
                  </YStack>
                  {creatingNew && (
                    <Ionicons name="checkmark-circle" size={24} color="white" />
                  )}
                </XStack>
              </Card>
            </Pressable>

            {/* Search Bar */}
            {!creatingNew && (
              <View style={{ marginBottom: 16 }}>
                <TextInput
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder="Search data models..."
                  placeholderTextColor={colors.textSecondary}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: colors.cardBackground,
                    color: colors.text,
                    fontSize: 14,
                    borderWidth: 1,
                    borderColor: colors.border
                  }}
                />
              </View>
            )}

            {/* Data Models List */}
            {!creatingNew && (
              <View style={{ flex: 1, maxHeight: 300 }}>
                {loading ? (
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                    <ActivityIndicator size="large" color={colors.accent} />
                    <Text style={{ color: colors.textSecondary, marginTop: 12 }}>
                      Loading data models...
                    </Text>
                  </View>
                ) : filteredModels.length === 0 ? (
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                    <MaterialIcons name="folder-open" size={64} color={colors.textSecondary} />
                    <Text style={{ color: colors.text, marginTop: 12, fontSize: 16, fontWeight: '600' }}>
                      No Data Models Found
                    </Text>
                    <Text style={{ color: colors.textSecondary, marginTop: 8, textAlign: 'center' }}>
                      Create a new model or adjust your search
                    </Text>
                  </View>
                ) : (
                  <FlatList
                    data={filteredModels}
                    renderItem={renderModelItem}
                    keyExtractor={(item) => item._id}
                    showsVerticalScrollIndicator={true}
                    contentContainerStyle={{ paddingBottom: 8 }}
                  />
                )}
              </View>
            )}

            {/* Action Buttons */}
            <XStack gap={12} marginTop={20}>
              <Button
                onPress={() => {
                  setVisible(false);
                  resetState();
                }}
                buttonText="Cancel"
                customStyle={{
                  flex: 1,
                  backgroundColor: colors.cardBackground,
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderRadius: 12,
                  minHeight: 48
                }}
                textColor={colors.text}
              />
              <Button
                onPress={handleConfirm}
                buttonText={submitting ? 'Adding...' : `Add ${selectedPOIs.length} POIs`}
                disabled={(!creatingNew && !selectedModelId) || submitting}
                customStyle={{
                  flex: 1,
                  backgroundColor: colors.smartBlue,
                  borderRadius: 12,
                  minHeight: 48,
                  opacity: (!creatingNew && !selectedModelId) || submitting ? 0.5 : 1
                }}
                textColor="white"
                icon={submitting ? undefined : 'check'}
                iconColor="white"
              />
            </XStack>
          </Card>
        </View>

        {/* Compatibility Warning Modal */}
        {showCompatibilityWarning && compatibilityReport && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 20
            }}
          >
            <Card
              backgroundColor={colors.background}
              padding={24}
              borderRadius={16}
              width="100%"
              maxWidth={550}
              maxHeight="70%"
              shadowColor="#000"
              shadowOffset={{ width: 0, height: 8 }}
              shadowOpacity={0.4}
              shadowRadius={16}
              elevation={16}
            >
              {/* Warning Header */}
              <XStack alignItems="center" gap={12} marginBottom={20}>
                <View
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 28,
                    backgroundColor: '#f59e0b',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <FontAwesome5 name="exclamation-triangle" size={28} color="white" />
                </View>
                <YStack flex={1}>
                  <Title style={{ color: colors.text, fontSize: 20, fontWeight: '700' }}>
                    Field Compatibility Warning
                  </Title>
                  <Text style={{ color: colors.textSecondary, fontSize: 13, marginTop: 4 }}>
                    Some fields may not match the selected model
                  </Text>
                </YStack>
              </XStack>

              {/* Compatibility Stats */}
              <Card
                backgroundColor={compatibilityReport.matchRate >= 60 ? '#fef3c7' : '#fee2e2'}
                padding={16}
                borderRadius={12}
                marginBottom={16}
              >
                <YStack gap={8}>
                  <XStack justifyContent="space-between" alignItems="center">
                    <Text style={{ color: '#78350f', fontWeight: '600', fontSize: 14 }}>
                      Match Rate
                    </Text>
                    <Text style={{ color: '#78350f', fontWeight: '700', fontSize: 18 }}>
                      {compatibilityReport.matchRate}%
                    </Text>
                  </XStack>
                  <XStack justifyContent="space-between">
                    <Text style={{ color: '#78350f', fontSize: 13 }}>
                      Matched: {compatibilityReport.matchedFields}/{compatibilityReport.totalFields} fields
                    </Text>
                    <Text style={{ color: '#78350f', fontSize: 13 }}>
                      Type mismatches: {compatibilityReport.typeMismatches}
                    </Text>
                  </XStack>
                </YStack>
              </Card>

              {/* Recommendation */}
              <Card
                backgroundColor={colors.cardBackground}
                padding={12}
                borderRadius={8}
                marginBottom={16}
              >
                <Text style={{ color: colors.text, fontSize: 13, lineHeight: 18 }}>
                  💡 {compatibilityReport.recommendation}
                </Text>
              </Card>

              {/* Mismatch Details */}
              {compatibilityReport.mismatches.length > 0 && (
                <View style={{ marginBottom: 16, maxHeight: 200 }}>
                  <Text style={{ color: colors.text, fontWeight: '600', fontSize: 14, marginBottom: 8 }}>
                    Issues Found ({compatibilityReport.mismatches.length}):
                  </Text>
                  <ScrollView style={{ maxHeight: 180 }}>
                    {compatibilityReport.mismatches.slice(0, 5).map((mismatch: any, index: number) => (
                      <Card
                        key={index}
                        backgroundColor={mismatch.severity === 'high' ? '#fee2e2' : '#fef3c7'}
                        padding={10}
                        borderRadius={8}
                        marginBottom={8}
                      >
                        <XStack alignItems="flex-start" gap={8}>
                          <FontAwesome5
                            name={mismatch.severity === 'high' ? 'times-circle' : 'info-circle'}
                            size={14}
                            color={mismatch.severity === 'high' ? '#dc2626' : '#d97706'}
                            style={{ marginTop: 2 }}
                          />
                          <YStack flex={1}>
                            <Text
                              style={{
                                color: mismatch.severity === 'high' ? '#7f1d1d' : '#78350f',
                                fontSize: 12,
                                fontWeight: '600'
                              }}
                            >
                              {mismatch.field || mismatch.poiField}
                            </Text>
                            <Text
                              style={{
                                color: mismatch.severity === 'high' ? '#991b1b' : '#92400e',
                                fontSize: 11,
                                marginTop: 2
                              }}
                            >
                              {mismatch.message}
                            </Text>
                          </YStack>
                        </XStack>
                      </Card>
                    ))}
                    {compatibilityReport.mismatches.length > 5 && (
                      <Text style={{ color: colors.textSecondary, fontSize: 11, textAlign: 'center', marginTop: 4 }}>
                        + {compatibilityReport.mismatches.length - 5} more issues
                      </Text>
                    )}
                  </ScrollView>
                </View>
              )}

              {/* Action Buttons */}
              <YStack gap={10}>
                <Button
                  onPress={() => {
                    setShowCompatibilityWarning(false);
                    setCreatingNew(true);
                    setSelectedModelId(null);
                  }}
                  buttonText="Create New Model Instead"
                  customStyle={{
                    backgroundColor: colors.success,
                    borderRadius: 12,
                    minHeight: 44
                  }}
                  textColor="white"
                  icon="plus-circle"
                  iconColor="white"
                />
                <XStack gap={10}>
                  <Button
                    onPress={() => {
                      setShowCompatibilityWarning(false);
                      setCompatibilityReport(null);
                    }}
                    buttonText="Go Back"
                    customStyle={{
                      flex: 1,
                      backgroundColor: colors.cardBackground,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderRadius: 12,
                      minHeight: 44
                    }}
                    textColor={colors.text}
                  />
                  <Button
                    onPress={async () => {
                      setShowCompatibilityWarning(false);
                      await proceedWithAddition();
                    }}
                    buttonText="Proceed Anyway"
                    customStyle={{
                      flex: 1,
                      backgroundColor: '#f59e0b',
                      borderRadius: 12,
                      minHeight: 44
                    }}
                    textColor="white"
                    icon="arrow-forward"
                    iconColor="white"
                  />
                </XStack>
              </YStack>
            </Card>
          </View>
        )}
      </Modal>
    );
  }
);

DataModelSelectionModal.displayName = 'DataModelSelectionModal';

export default DataModelSelectionModal;

